/**
 * Scoring: rest position → golf strokes (HANDOFF §3.5) — Agent B (shot pipeline).
 *
 * PURE: maps a settled ball position relative to pin/green-centre to a
 * {@link ScoreResult}. NO DOM, NO randomness.
 *
 * PHASE 1 CONTRACT — Agent A calls `scoreShot` from the reducer when a ball
 * settles; the stroke delta comes from {@link STROKE_DELTA}. Radii are UNSTABLE
 * (§5 / §5b) and come from SCORING in ./constants.ts — do NOT hardcode them here.
 */
import type { Green, Hazard, HazardType, ScoreResult, ShapeRegion, Vec2 } from './types.ts';
import { STROKE_DELTA as STROKE_DELTA_CONST, SCORING } from './constants.ts';
import { pointInGreen, pointInScaledGreen } from './greens.ts';

/** Stroke deltas per result (§3.5). Lowest total wins. Re-exported from constants.ts. */
export const STROKE_DELTA: Readonly<Record<ScoreResult, number>> = STROKE_DELTA_CONST;

function dist(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** The kind of hazard a settled ball rests in, or null if none — so the reducer can
 *  treat water specially (splash + sink) while scoring stays a flat +2 (§3.7). Trees are
 *  NOT rest-catch hazards (they block in flight — see physics `situationalFate`), so skipped. */
export function hazardKindAt(restPos: Vec2, green: Green): HazardType | null {
  for (const hazard of green.hazards) {
    if (hazard.type === 'tree') continue;
    if (pointInGreen(restPos, hazardRegion(hazard))) return hazard.type;
  }
  return null;
}

/** View a hazard as a {@link ShapeRegion} (its `pos` is the centre) so the shared
 *  procedural-shape test scores its SHAPE — the same outline the renderer traces. */
function hazardRegion(hz: Hazard): ShapeRegion {
  return {
    center: hz.pos,
    radius: hz.radius,
    rx: hz.rx,
    ry: hz.ry,
    rot: hz.rot,
    lobe: hz.lobe,
    lobePhase: hz.lobePhase,
  };
}

/**
 * Classify a settled ball position into a golf result (§3.5).
 *
 * `zoneScale` (from the shot's TEE) scales EVERY zone: a LONG shot gets the largest
 * eagle/birdie/par/bogey zones (most forgiving), a SHORT approach the smallest. The
 * pin-distance rings (eagle ⊂ birdie) and the procedural-shape PAR/BOGEY tests all scale
 * by it; only the ACE cup is fixed. mid shots use zoneScale = 1 (the §5b baseline).
 */
export function scoreShot(restPos: Vec2, green: Green, zoneScale = 1): ScoreResult {
  // A ball that comes to rest in a hazard is a +2 (§3.7), overriding the zones —
  // tested against the hazard's procedural SHAPE (what you see is what scores). Trees are
  // fly-over OBSTACLES (resolved in flight as a knockdown), never a rest-in-disc double.
  for (const hazard of green.hazards) {
    if (hazard.type === 'tree') continue;
    if (pointInGreen(restPos, hazardRegion(hazard))) return 'double';
  }

  const dp = dist(restPos, green.pin);

  const aceR = SCORING.aceRadius; // the cup is fixed — a hole-in-one is the hole
  const eagleR = SCORING.pinRefRadius * SCORING.eagleRadiusFactor * zoneScale;
  const birdieR = SCORING.pinRefRadius * SCORING.birdieRadiusFactor * zoneScale;

  if (dp < aceR) return 'ace';
  if (dp < eagleR) return 'eagle';
  if (dp < birdieR) return 'birdie';
  // Par: on the green SHAPE (scaled by the tee's zone). Bogey: that shape expanded by
  // bogeyMargin (a uniform scale about the centre — monotonic, so par ⊂ bogey).
  if (pointInScaledGreen(restPos, green, zoneScale)) return 'par';
  const bogeyScale = ((green.radius + SCORING.bogeyMargin) / green.radius) * zoneScale;
  if (pointInScaledGreen(restPos, green, bogeyScale)) return 'bogey';
  return 'double';
}
